import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Edit from '@material-ui/icons/Edit';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import TableSection from './TableSection';
import ContactDataPopup from './ContactDataPopup';
import { RootState } from '../../../redux/root';
import { StepType, WikiInspectionType } from '../../../api/jurisdiction';
import FileUpload from './FileUpload';
import { useGetJurisdictionRequestResultQuery } from '../../../redux/rtkQuery/apiWikiInspectionSlice';
import { setSelectedJurisdiction } from '../../../redux/slices/wikiSlice';
import { useGetJurisdictionByIdQuery } from '../../../redux/rtkQuery/apiWikiSlice';

const Inspections = () => {
  // @ts-ignore
  const { wikiId } = useParams();
  const [showpop, setShowPop] = useState<boolean>(false);
  const selectedJurisdiction = useSelector((s:RootState) => s.wikiSlice.selectedJurisdiction);
  const [defaultValues, setDefaultValues] = useState<WikiInspectionType>({} as WikiInspectionType);
  const [requestSteps, setRequestSteps] = useState<StepType[]>([]);
  const [resultSteps, setResultSteps] = useState<StepType[]>([]);
  const { data } = useGetJurisdictionRequestResultQuery({ id: wikiId });

  useEffect(() => {
    if (data) {
      const reqs: StepType[] = [];
      const ress: StepType[] = [];
      data.forEach((item:StepType) => {
        if (item.recordType === 'Request') {
          reqs.push(item);
        }
        if (item.recordType === 'Result') {
          ress.push(item);
        }
      });
      setRequestSteps(reqs);
      setResultSteps(ress);
    }
  }, [data]);
  useEffect(() => {
    if (selectedJurisdiction && selectedJurisdiction.inspection) {
      setDefaultValues(selectedJurisdiction.inspection);
    }
  }, [selectedJurisdiction]);
  const onClick = e => {
    setShowPop(!showpop);
  };

  return (
    <>
      <Card>
        <SectionTitle>Inspections</SectionTitle>
        <ColumnsGrid>
          <SubSection>
            <SubTitle>Codes Document</SubTitle>
            <FileUpload />
          </SubSection>
          <SubSection>
            <SubTitle>
              Contact
              <EditIconAnchor>
                <EditIconButton type="button" title="Edit contact" onClick={onClick}>
                  <Edit fontSize="small" />
                </EditIconButton>
                {showpop && (
                  <ContactDataPopup
                    defaultValues={defaultValues}
                    setDefaultValues={setDefaultValues}
                    setShowPopup={setShowPop}
                  />
                )}
              </EditIconAnchor>
            </SubTitle>
            <FieldGrid>
              <FieldLabel>BD Contact Person</FieldLabel>
              <StaticValue>{defaultValues.boContactPerson || '—'}</StaticValue>
              <FieldLabel>BD Insp Email</FieldLabel>
              <StaticValue>{defaultValues.boInspectionEmail || '—'}</StaticValue>
              <FieldLabel>BD Insp Phone</FieldLabel>
              <StaticValue>{defaultValues.boInspectionPhone || '—'}</StaticValue>
            </FieldGrid>
          </SubSection>
        </ColumnsGrid>
      </Card>

      <TablesGrid>
        <TableSection header="Inspection Request" rows={requestSteps} inspectionType="Request" />
        <TableSection header="Inspection Result" rows={resultSteps} inspectionType="Result" />
      </TablesGrid>
    </>
  );
};

export default Inspections;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol'`;

const Card = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  margin-bottom: 20px;
  font-family: ${FONT_STACK};
`;

const SectionTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  margin-bottom: 20px;
`;

const ColumnsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 32px;
  align-items: stretch;
`;

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  min-width: 0;
  box-sizing: border-box;
`;

const SubTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const EditIconAnchor = styled.div`
  position: relative;
  display: inline-flex;
`;

const EditIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--neutral-0);
  color: var(--neutral-500);
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-300);
    color: var(--primary-600);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`;

export const FieldLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--neutral-600);
`;

export const Label = FieldLabel;

export const StaticValue = styled.div`
  width: 100%;
  min-height: 40px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-size: 0.875rem;
  color: var(--mainText);
  box-sizing: border-box;
  word-break: break-word;
`;

const TablesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
`;
